<template>
  <Teleport to="body">
    <Transition name="popover-fade">
      <div
          v-if="visible"
          ref="popoverRef"
          class="custom-popover"
          :class="popoverClass"
          :style="popoverStyle"
          @click.stop
      >
        <div class="custom-popover__content">
          <slot/>
        </div>
        <div class="custom-popover__arrow" :style="arrowStyle"/>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
export default {
  name: 'CustomPopover',

  props: {
    offsetX: {
      type: Number,
      default: 15
    },
    offsetY: {
      type: Number,
      default: 10
    },
    popoverClass: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      visible: false,
      triggerRect: null,
      popoverRect: null,
      popoverPosition: {
        top: 0,
        left: 0
      },
      arrowPosition: {
        left: '50%',
        right: 'auto'
      }
    }
  },

  computed: {
    popoverStyle() {
      return {
        top: `${this.popoverPosition.top}px`,
        left: `${this.popoverPosition.left}px`
      }
    },
    arrowStyle() {
      return {
        left: this.arrowPosition.left,
        right: this.arrowPosition.right
      }
    }
  },

  mounted() {
    document.addEventListener('click', this.handleClickOutside)
    window.addEventListener('resize', this.handleResize)
    window.addEventListener('scroll', this.handleResize, true)
  },

  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside)
    window.removeEventListener('resize', this.handleResize)
    window.removeEventListener('scroll', this.handleResize, true)
  },

  methods: {
    open(event) {
      if (!event) return

      this.triggerRect = {
        x: event.clientX,
        y: event.clientY
      }

      this.visible = true

      this.$nextTick(() => {
        this.calculatePosition()
      })
    },

    close() {
      this.visible = false
      this.triggerRect = null
      this.popoverRect = null
    },

    toggle(event) {
      if (this.visible) {
        this.close()
      } else {
        this.open(event)
      }
    },

    calculatePosition() {
      if (!this.$refs.popoverRef || !this.triggerRect) return

      this.popoverRect = this.$refs.popoverRef.getBoundingClientRect()

      let top = this.triggerRect.y + this.offsetY
      let left = this.triggerRect.x + this.offsetX

      const rightEdge = left + this.popoverRect.width
      const viewportWidth = window.innerWidth

      let arrowLeftOffset = '50%'
      let arrowRightOffset = 'auto'

      if (rightEdge > viewportWidth) {
        const overflow = rightEdge - viewportWidth + this.offsetX
        left = Math.max(this.offsetX, left - overflow - this.offsetX)
        const clickXRelative = this.triggerRect.x - left
        if (clickXRelative > 0 && clickXRelative < this.popoverRect.width) {
          arrowLeftOffset = `${clickXRelative}px`
          arrowRightOffset = 'auto'
        }
      }

      const bottomEdge = top + this.popoverRect.height
      const viewportHeight = window.innerHeight

      if (bottomEdge > viewportHeight) {
        top = this.triggerRect.y - this.popoverRect.height - this.offsetY
      }

      if (top < 10) {
        top = 10
      }

      this.popoverPosition = {top, left}
      this.arrowPosition = {
        left: arrowLeftOffset,
        right: arrowRightOffset
      }
    },

    handleClickOutside(event) {
      if (!this.visible) return
      if (!event.target.classList.contains("help-button")) {
        const popoverElement = this.$refs.popoverRef
        if (popoverElement && !popoverElement.contains(event.target)) {
          this.close()
        }
      }
    },

    handleResize() {
      if (this.visible && this.triggerRect) {
        this.calculatePosition()
      }
    }
  }
}
</script>

<style scoped>
.popover-fade-enter-active,
.popover-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.popover-fade-enter-from,
.popover-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.custom-popover {
  position: absolute;
  z-index: 9999;
  max-width: 450px;
  min-width: 260px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.02);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: 14px;
  line-height: 1.5;
  color: #1f2937;
  pointer-events: auto;
}

.custom-popover__content {
  padding: 16px;
  max-height: 400px;
  overflow-y: auto;
}

.custom-popover__arrow {
  position: absolute;
  top: -8px;
  width: 12px;
  height: 12px;
  background: white;
  transform: rotate(45deg);
  box-shadow: -2px -2px 4px rgba(0, 0, 0, 0.05);
  pointer-events: none;
}

.custom-popover__content h3 {
  margin-top: 0;
  margin-bottom: 12px;
  font-size: 16px;
  font-weight: 600;
}

.custom-popover__content p {
  margin-bottom: 12px;
}

.custom-popover__content b {
  font-weight: 600;
  color: #111827;
}

.custom-popover__content .help-popover__spaced {
  margin-top: 12px;
}

.custom-popover__content .help-popover__count-rule {
  margin-top: 12px;
  padding-top: 8px;
  border-top: 1px solid #e5e7eb;
  font-size: 13px;
  color: #6b7280;
}

.custom-popover__content::-webkit-scrollbar {
  width: 6px;
}

.custom-popover__content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.custom-popover__content::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.custom-popover__content::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

</style>
<style lang="scss">
.custom-popover {
  h3 {
    margin: 0 0 8px;
    font-size: 14px;
    color: #1F2937;
    font-weight: bold;
  }
  b{
    color: #4B5563;
  }

  .help-popover__count-rule {
    margin-top: 12px;
    padding: 8px 12px;
    border-radius: 4px;
    background: #EFF6FF;
    border: 1px solid #BED2F4;
    color: #1F2937;
    font-weight: 600;
  }
}
</style>